import LockResetIcon from "@mui/icons-material/LockReset";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { Avatar, Box, IconButton, InputAdornment, Stack, Typography } from "@mui/material";
import { memo, useCallback, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import Button from "../../Button";
import FormInput from "../../Inputs/FormInput";
import useFetch from "../../../../hooks/useFetch";
import ChangePasswordService from "../../../../services/changePassword.service";
import { notificationSliceActions } from "../../../../store/slices/notification";
import { useAppDispatch } from "../../../../store/store";
import ModalComponent from "../../Modal";
import { changePasswordSchema, TChangePasswordForm } from "./changePasswordSchema";
import { IChangePasswordModal } from "./models";

const translationPath = "page.changePassword.";

const changePasswordService = new ChangePasswordService();

const PasswordField = ({
  name,
  label,
  autoFocus,
}: {
  name: string;
  label: string;
  autoFocus?: boolean;
}) => {
  const [show, setShow] = useState(false);

  return (
    <FormInput
      size="small"
      name={name}
      label={label}
      fullWidth
      autoFocus={autoFocus}
      type={show ? "text" : "password"}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              size="small"
              edge="end"
              tabIndex={-1}
              aria-label={label}
              onClick={() => setShow((s) => !s)}
            >
              {show ? (
                <VisibilityOff fontSize="small" />
              ) : (
                <Visibility fontSize="small" />
              )}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
};

export const ChangePasswordModal = memo(
  ({ open, onClose }: IChangePasswordModal) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const { sendRequest, loading } = useFetch(changePasswordService.create);

    const methods = useForm<TChangePasswordForm>({
      resolver: zodResolver(changePasswordSchema),
    });

    const { handleSubmit, reset } = methods;

    useEffect(() => {
      if (!open) reset();
    }, [open, reset]);

    const onSubmitHandler = useCallback(
      async (values: TChangePasswordForm) => {
        const { success } = await sendRequest({
          currentPassword: values.currentPassword,
          newPassword: values.newPassword,
        });
        if (success) {
          dispatch(
            notificationSliceActions.showNotification({
              message: `${t(translationPath + "success_message")}`,
              type: "success",
            })
          );
          onClose();
        }
      },
      [sendRequest, dispatch, t, onClose]
    );

    return (
      <ModalComponent open={open} onClose={onClose} dialogMaxWidth={"xs"}>
        <FormProvider {...methods}>
          <Box
            component="form"
            autoComplete="off"
            onSubmit={handleSubmit(onSubmitHandler)}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "100%",
              minWidth: { xs: "auto", sm: 340 },
            }}
          >
            <Avatar
              sx={(theme) => ({
                bgcolor:
                  theme.palette.mode === "light"
                    ? theme.palette.primary.light
                    : theme.palette.primary.dark,
                color: theme.palette.primary.contrastText,
                width: 56,
                height: 56,
                mb: 1.5,
              })}
            >
              <LockResetIcon />
            </Avatar>
            <Typography variant="h6" textAlign="center">
              {t(translationPath + "change_password")}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              mb={3}
            >
              {t(translationPath + "subtitle")}
            </Typography>

            <Stack spacing={2} width="100%">
              <PasswordField
                name="currentPassword"
                label={t(translationPath + "actual_password")}
                autoFocus
              />
              <PasswordField
                name="newPassword"
                label={t(translationPath + "new_password")}
              />
              <PasswordField
                name="confirmNewPassword"
                label={t(translationPath + "confirm_new_password")}
              />
            </Stack>

            <Box sx={{ display: "flex", gap: 1, width: "100%", mt: 3 }}>
              <Button
                fullWidth
                variant="outlined"
                size="small"
                disabled={loading}
                onClick={onClose}
              >
                {t(translationPath + "cancel")}
              </Button>
              <Button
                fullWidth
                variant="contained"
                size="small"
                type="submit"
                loading={loading}
              >
                {t(translationPath + "save")}
              </Button>
            </Box>
          </Box>
        </FormProvider>
      </ModalComponent>
    );
  }
);

export default ChangePasswordModal;
